/**
 * LiveKit React Native transport — the real `TransportAdapter` over
 * `@livekit/react-native`, behavior-matched to the web adapter (which is
 * itself matched to production, @desivocal/agents-cdn).
 *
 * The wire behavior is deliberately identical to `web/src/transport/
 * livekit-transport.ts` — same Room options, same double-send chat, same
 * agent-classification and session-ended rules — because both talk to the same
 * backend and core drives them through the same port. Read that file's parity
 * notes; they apply here verbatim.
 *
 * Only what the platform forces differs:
 *  - remote audio needs no elements. Mobile plays subscribed audio through the
 *    OS, so `<RoomAudioRenderer />`'s job becomes owning an audio SESSION
 *    (see platform/audio-session.ts) rather than attaching media elements;
 *  - `registerGlobals()` must run before a Room is constructed — the adapter
 *    does it so a missing app-entry call is not a mysterious runtime failure;
 *  - autoplay policy has no mobile equivalent, so the web adapter's
 *    `startAudio()` recovery has no counterpart.
 */

import { registerGlobals } from "@livekit/react-native";
import {
  ConnectionState as LKConnectionState,
  Room,
  RoomEvent,
  type DataPacket_Kind,
  type Participant,
  type RemoteParticipant,
  type RoomOptions,
  type RpcInvocationData,
  type TextStreamHandler,
  type TranscriptionSegment as LKTranscriptionSegment,
} from "livekit-client";

import type { ConnectionState, IncomingChatMessage, IncomingTextStream, RpcInvocation, TranscriptionSegment, TransportAdapter } from "@ringg/core";

import { createAudioSession, type AudioSessionPort } from "../platform/audio-session";
import { findAgentParticipant, isAgentParticipant } from "./agent";
import { CHAT_TOPIC, LEGACY_CHAT_TOPIC, decodeLegacyChatMessage, encodeLegacyChatMessage, serverSupportsDataStreams } from "./legacy-chat";

export interface LiveKitTransportOptions {
  /**
   * Extra LiveKit `RoomOptions` merged over the parity defaults
   * (`dynacast: true, adaptiveStream: true`). Rarely needed.
   */
  roomOptions?: RoomOptions;
  /**
   * Own the native audio session for the duration of a call — the mobile
   * equivalent of production's `<RoomAudioRenderer />`. Default true; disable
   * when the host app already manages an audio session (an in-app player, a
   * CallKit/ConnectionService integration).
   */
  manageAudioSession?: boolean;
  /**
   * Call `registerGlobals()` before constructing the Room. Default true.
   * Set false when the app already calls it at its entry point — it is
   * idempotent, so this is a formality rather than a correctness switch.
   */
  registerGlobals?: boolean;
}

export interface LiveKitTransport {
  transport: TransportAdapter;
  /**
   * The underlying Room, for presentational concerns the port cannot express
   * (visualizer track handles, `isMicrophoneEnabled` readback — spec §11).
   * State-changing calls must keep going through the transport/core.
   */
  room: Room;
  /** Tears down listeners, handlers and the audio session; disconnects the room. */
  dispose(): void;
}

/** Production buffers 100 transcription segments; keep headroom past that. */
const FIRST_SEEN_LIMIT = 200;

export const createLiveKitTransport = (options: LiveKitTransportOptions = {}): LiveKitTransport => {
  // Must precede `new Room()`: livekit-client resolves the WebRTC globals at
  // construction time, and RN has none until this runs.
  if (options.registerGlobals !== false) registerGlobals();

  // Same construction as production (AppWrapper.tsx): one Room per widget
  // lifetime, never recreated between calls.
  const room = new Room({ dynacast: true, adaptiveStream: true, ...options.roomOptions });

  const audioSession: AudioSessionPort | undefined = options.manageAudioSession === false ? undefined : createAudioSession();

  const chatHandlers = new Set<(message: IncomingChatMessage) => void>();
  const registeredRpcMethods = new Set<string>();
  const registeredTextStreamTopics = new Set<string>();

  // ─── Transcription first-seen timestamps ──────────────────────────────────
  // The same segment id streams in repeatedly as STT refines; `receivedAt`
  // must stay stable across refinements (production stamps wall-clock at
  // first receipt and reuses it — segmentToChatMessage keeps the original
  // timestamp). Bounded so a long call can't grow the map forever.
  const firstSeenAt = new Map<string, number>();
  const firstSeen = (id: string): number => {
    const existing = firstSeenAt.get(id);
    if (existing !== undefined) return existing;
    const now = Date.now();
    firstSeenAt.set(id, now);
    if (firstSeenAt.size > FIRST_SEEN_LIMIT) {
      const oldest = firstSeenAt.keys().next();
      if (!oldest.done) firstSeenAt.delete(oldest.value);
    }
    return now;
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * livekit-client throws when a topic already has a handler — production
   * re-registers across renders without hitting this because the hooks
   * unregister on cleanup. The adapter makes registration idempotent instead.
   */
  const setTextStreamHandler = (topic: string, handler: TextStreamHandler): void => {
    if (registeredTextStreamTopics.has(topic)) room.unregisterTextStreamHandler(topic);
    room.registerTextStreamHandler(topic, handler);
    registeredTextStreamTopics.add(topic);
  };

  const emitChat = (message: IncomingChatMessage): void => {
    chatHandlers.forEach((handler) => handler(message));
  };

  /**
   * Chat classification (App.tsx:1187–1191): identity match against the
   * resolved agent; a non-self sender defaults to the agent while no agent
   * participant is resolvable (production's `!isSelf` fallback).
   */
  const classifyRemoteAsAgent = (identity: string): boolean => {
    const participant = room.getParticipantByIdentity(identity);
    return participant ? isAgentParticipant(participant) : true;
  };

  const mapConnectionState = (state: LKConnectionState): ConnectionState => {
    switch (state) {
      case LKConnectionState.Connecting:
        return "connecting";
      case LKConnectionState.Connected:
        return "connected";
      case LKConnectionState.Reconnecting:
      case LKConnectionState.SignalReconnecting:
        // Core's union has no signal-reconnect notion — coerce (spec §9).
        return "reconnecting";
      case LKConnectionState.Disconnected:
        return "disconnected";
    }
  };

  // ─── Incoming chat (both channels, merged — components-core `setupChat`) ──

  // Primary channel: `lk.chat` text streams. The server never loops a
  // participant's own `sendText` back to them, and core commits the local
  // echo itself, so only remote messages are surfaced (identity-guarded
  // anyway for belt and braces). Emitting once per completed stream is the
  // spec-sanctioned simplification of setupChat's chunked replace-by-id.
  setTextStreamHandler(CHAT_TOPIC, (reader, participantInfo) => {
    void (async () => {
      let message: string;
      try {
        message = await reader.readAll();
      } catch {
        return; // stream aborted mid-flight — nothing to surface
      }
      const identity = participantInfo.identity;
      if (identity === room.localParticipant.identity) return;
      emitChat({
        message,
        isSelf: false,
        isAgent: classifyRemoteAsAgent(identity),
        senderName: room.getParticipantByIdentity(identity)?.name,
        timestamp: reader.info.timestamp,
      });
    })();
  });

  // Legacy channel: `lk-chat-topic` data packets from old servers. Messages
  // flagged `ignoreLegacy` were also sent as a text stream and are dropped
  // here — that flag is the dedup between the two channels.
  const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant, _kind?: DataPacket_Kind, topic?: string): void => {
    if (topic !== LEGACY_CHAT_TOPIC) return;
    const legacy = decodeLegacyChatMessage(payload);
    if (!legacy || legacy.ignoreLegacy) return;
    // DataReceived only fires for remote senders; guard against echoes anyway.
    if (participant && participant.identity === room.localParticipant.identity) return;
    emitChat({
      message: legacy.message,
      isSelf: false,
      isAgent: participant ? isAgentParticipant(participant) : true,
      senderName: participant?.name,
      timestamp: legacy.timestamp,
    });
  };
  room.on(RoomEvent.DataReceived, handleDataReceived);

  // Segment ids never survive a session; drop the timestamp cache with them,
  // and hand the audio session back to the OS the moment the room drops —
  // `destroy()` releasing the mic immediately is a release-blocking invariant.
  const handleRoomDisconnected = (): void => {
    firstSeenAt.clear();
    void audioSession?.stop();
  };
  room.on(RoomEvent.Disconnected, handleRoomDisconnected);

  // ─── TransportAdapter ─────────────────────────────────────────────────────

  const transport: TransportAdapter = {
    async connect(url: string, token: string): Promise<void> {
      // The session is activated BEFORE connecting: acquiring it afterwards
      // races the first inbound audio frame and clips the agent's greeting.
      await audioSession?.start();
      try {
        // Default ConnectOptions, like production (autoSubscribe: true).
        // Failures propagate — core rolls back its optimistic session phase.
        await room.connect(url, token);
      } catch (error) {
        // Never leave the session held open by a connect that never landed.
        await audioSession?.stop();
        throw error;
      }
    },

    async disconnect(): Promise<void> {
      // Safe in every state: livekit no-ops when already disconnected, which
      // covers the onSessionEnded → endCall() → disconnect() re-entry after
      // a remote hangup (spec §11.8). `handleRoomDisconnected` releases the
      // audio session on the way out.
      await room.disconnect();
    },

    async prepareConnection(url: string): Promise<void> {
      // Best-effort prewarm (App.tsx: `.catch(() => {})`) — a failure must
      // never surface or block the later connect.
      try {
        await room.prepareConnection(url);
      } catch {
        /* best-effort only */
      }
    },

    async setMicrophoneEnabled(enabled: boolean): Promise<void> {
      // Handles track acquisition + publication; room.disconnect() tears the
      // track down — no manual publish/unpublish anywhere (spec §2).
      await room.localParticipant.setMicrophoneEnabled(enabled);
    },

    async sendChatMessage(text: string): Promise<void> {
      // 1:1 with components-core `setupChat.send` — both sends, same order.
      // Core commits the local echo; nothing is emitted via onChatMessage.
      const info = await room.localParticipant.sendText(text, { topic: CHAT_TOPIC });
      await room.localParticipant.publishData(
        encodeLegacyChatMessage({
          id: info.id,
          timestamp: Date.now(),
          message: text,
          ignoreLegacy: serverSupportsDataStreams(room),
        }),
        { reliable: true, topic: LEGACY_CHAT_TOPIC },
      );
    },

    async performRpcToAgent(method: string, payload: string): Promise<string> {
      const agent = findAgentParticipant(room);
      if (!agent) {
        // Port contract: reject when no agent is present — core catches per
        // call-site (production warns + skips there).
        throw new Error(`Cannot perform RPC "${method}": no agent participant in the room`);
      }
      return room.localParticipant.performRpc({
        destinationIdentity: agent.identity,
        method,
        payload,
      });
    },

    registerRpcMethod(method: string, handler: (invocation: RpcInvocation) => Promise<string>): void {
      // livekit throws on duplicate registration — keep it idempotent.
      if (registeredRpcMethods.has(method)) room.unregisterRpcMethod(method);
      room.registerRpcMethod(method, (data: RpcInvocationData) =>
        // Handler rejections must propagate: livekit surfaces non-RpcError
        // throws to the caller as RPC application error 1500 (spec §4).
        handler({ method, payload: data.payload, callerIdentity: data.callerIdentity }),
      );
      registeredRpcMethods.add(method);
    },

    unregisterRpcMethod(method: string): void {
      room.unregisterRpcMethod(method);
      registeredRpcMethods.delete(method);
    },

    registerTextStreamHandler(topic: string, handler: (stream: IncomingTextStream) => void): void {
      // Callable pre-connect (required: late `lk.transcription` registration
      // drops the agent greeting). Note: registering `lk.chat` here replaces
      // the adapter's internal chat wiring — core never does.
      setTextStreamHandler(topic, (reader, participantInfo) => {
        handler({
          readAll: () => reader.readAll(),
          participantIdentity: participantInfo.identity,
          isAgent: classifyRemoteAsAgent(participantInfo.identity),
        });
      });
    },

    unregisterTextStreamHandler(topic: string): void {
      room.unregisterTextStreamHandler(topic);
      registeredTextStreamTopics.delete(topic);
    },

    onConnectionStateChange(handler: (state: ConnectionState) => void): () => void {
      const listener = (state: LKConnectionState): void => {
        handler(mapConnectionState(state));
      };
      room.on(RoomEvent.ConnectionStateChanged, listener);
      return () => {
        room.off(RoomEvent.ConnectionStateChanged, listener);
      };
    },

    onSessionEnded(handler: () => void): () => void {
      // Production registers BOTH and never checks identity/kind: any remote
      // participant leaving ends the call (only the agent is ever remote),
      // and so does the room dropping (spec §1.4). Consumers guard their own
      // idempotency (core: `if (session.isSessionLive)`).
      const handleParticipantDisconnected = (_participant: RemoteParticipant): void => {
        handler();
      };
      const handleDisconnected = (): void => {
        handler();
      };
      room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.on(RoomEvent.Disconnected, handleDisconnected);
      return () => {
        room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
        room.off(RoomEvent.Disconnected, handleDisconnected);
      };
    },

    onTranscription(handler: (segment: TranscriptionSegment) => void): () => void {
      const listener = (segments: LKTranscriptionSegment[], participant?: Participant): void => {
        if (!participant) return;
        const isLocal = participant.isLocal;
        // Production only renders local + agent transcription (spec §8).
        if (!isLocal && !isAgentParticipant(participant)) return;
        for (const segment of segments) {
          handler({
            id: segment.id,
            text: segment.text,
            final: segment.final,
            isLocal,
            participantName: participant.name,
            receivedAt: firstSeen(segment.id),
          });
        }
      };
      room.on(RoomEvent.TranscriptionReceived, listener);
      return () => {
        room.off(RoomEvent.TranscriptionReceived, listener);
      };
    },

    onChatMessage(handler: (message: IncomingChatMessage) => void): () => void {
      chatHandlers.add(handler);
      return () => {
        chatHandlers.delete(handler);
      };
    },

    onMediaDevicesError(handler: () => void): () => void {
      // Production's use-microphone-permission.ts listens for this to surface
      // the "Error acquiring microphone permission..." message; core's session
      // store owns that copy now.
      const listener = (): void => {
        handler();
      };
      room.on(RoomEvent.MediaDevicesError, listener);
      return () => {
        room.off(RoomEvent.MediaDevicesError, listener);
      };
    },
  };

  const dispose = (): void => {
    room.off(RoomEvent.DataReceived, handleDataReceived);
    room.off(RoomEvent.Disconnected, handleRoomDisconnected);
    for (const topic of Array.from(registeredTextStreamTopics)) {
      room.unregisterTextStreamHandler(topic);
    }
    registeredTextStreamTopics.clear();
    for (const method of Array.from(registeredRpcMethods)) {
      room.unregisterRpcMethod(method);
    }
    registeredRpcMethods.clear();
    chatHandlers.clear();
    firstSeenAt.clear();
    // Disconnect first so the mic track is torn down before the OS session is
    // released — the reverse order leaves the recording indicator lit.
    void room.disconnect().finally(() => audioSession?.stop());
  };

  return { transport, room, dispose };
};
